import { useEffect } from 'react';
import type {
  ChannelRemoved,
  HistoryCleared,
  PublicUser,
  ReceiptUpdate,
  SessionRevoked,
} from '@orbit-sync/shared-types';
import { useAuthStore } from '../auth/authStore.js';
import { useMessageNotifications } from '../notifications/useMessageNotifications.js';
import { useSocket } from '../socket/SocketProvider.js';
import { useSettingsStore } from '../settings/settingsStore.js';
import { useChatStore } from './chatStore.js';
import { useDirectoryStore } from './directoryStore.js';
import { acknowledgeIncoming } from './receiptAcks.js';

/**
 * Bridges the socket to the chat store.
 *
 * Mounted once inside the authenticated shell, not per channel: the server joins
 * a socket to every one of its user's channel rooms at connect time, so a single
 * subscription receives traffic for all of them. Subscribing per open channel
 * would drop messages arriving for channels the user is not currently looking at.
 *
 * It is also the one place an incoming message is seen, so the system
 * notification is raised from here rather than from a second listener that would
 * have to be kept in step with this one.
 *
 * ARCHITECTURAL RULE: the handlers below are typed by the shared event maps in
 * @orbit-sync/shared-types. No payload shape is declared here.
 */
export function useChatSocket(): void {
  const { socket } = useSocket();
  const currentUserId = useAuthStore((state) => state.user?.id ?? null);
  const notify = useMessageNotifications();

  useEffect(() => {
    if (!socket) {
      return;
    }

    const {
      receiveMessage,
      receiveChatError,
      applyPresence,
      upsertChannel,
      applyEdit,
      applyDelete,
      applyReceipt,
      removeChannel,
      applyTyping,
      applyReactions,
    } = useChatStore.getState();
    const applySettings = useSettingsStore.getState().apply;

    const onMessage = (message: Parameters<typeof receiveMessage>[0]): void => {
      // Store first: the notifier reads `activeChannelId` and the channel list
      // from it, and clicking the notification navigates into a conversation the
      // sidebar must already know about.
      receiveMessage(message, currentUserId);
      notify(message);
      // It reached this client: the sender's tick doubles, or turns blue if
      // the conversation is on screen.
      acknowledgeIncoming(socket, message, currentUserId);
    };

    const onHistoryCleared = ({ channelId }: HistoryCleared): void => {
      useChatStore.getState().clearHistory(channelId);
    };

    const onChannelRemoved = ({ channelId }: ChannelRemoved): void => {
      removeChannel(channelId);
    };

    const onDirectoryUser = (user: PublicUser): void => {
      useDirectoryStore.getState().remember([user]);

      // Their own change, made in another tab: keep this tab's account in step.
      const { user: self } = useAuthStore.getState();

      if (self && self.id === user.id) {
        useAuthStore.setState({
          user: { ...self, displayName: user.displayName, avatarUpdatedAt: user.avatarUpdatedAt },
        });
      }
    };

    const onSessionRevoked = ({ reason }: SessionRevoked): void => {
      // The server ended this session and is about to close the socket. Sign
      // out now, with the reason, rather than leaving a shell whose every
      // request fails.
      useAuthStore.getState().logout(REVOKED_NOTICES[reason]);
    };

    const onReceipt = (update: ReceiptUpdate): void => {
      applyReceipt(update);

      // This user read the conversation on another device: the count here goes too.
      if (currentUserId && update.userId === currentUserId && update.readUpTo) {
        useChatStore.getState().markReadUpTo(update.channelId, update.readUpTo);
      }
    };

    // After a reconnect - Wi-Fi dropped, the laptop slept - messages may have
    // arrived that this client never heard. Reload the list (previews, unread
    // counts) and the open conversation, quietly.
    let connectedBefore = socket.connected;
    const onConnect = (): void => {
      if (connectedBefore) {
        void useChatStore.getState().resync();
      }

      connectedBefore = true;
    };

    socket.on('chat.message', onMessage);
    socket.on('chat.error', receiveChatError);
    // Someone opened a conversation that includes this user, or an administrator
    // renamed or changed one. The sidebar would otherwise be stale until reload.
    socket.on('chat.channel', upsertChannel);
    socket.on('chat.messageEdited', applyEdit);
    socket.on('chat.messageDeleted', applyDelete);
    socket.on('chat.receipt', onReceipt);
    socket.on('connect', onConnect);
    socket.on('chat.historyCleared', onHistoryCleared);
    socket.on('chat.channelRemoved', onChannelRemoved);
    socket.on('presence.update', applyPresence);
    socket.on('system.settings', applySettings);
    socket.on('system.sessionRevoked', onSessionRevoked);
    socket.on('chat.typing', applyTyping);
    socket.on('chat.reactions', applyReactions);
    socket.on('directory.user', onDirectoryUser);

    return () => {
      socket.off('chat.message', onMessage);
      socket.off('chat.error', receiveChatError);
      socket.off('chat.channel', upsertChannel);
      socket.off('chat.messageEdited', applyEdit);
      socket.off('chat.messageDeleted', applyDelete);
      socket.off('chat.receipt', onReceipt);
      socket.off('connect', onConnect);
      socket.off('chat.historyCleared', onHistoryCleared);
      socket.off('chat.channelRemoved', onChannelRemoved);
      socket.off('presence.update', applyPresence);
      socket.off('system.settings', applySettings);
      socket.off('system.sessionRevoked', onSessionRevoked);
      socket.off('chat.typing', applyTyping);
      socket.off('chat.reactions', applyReactions);
      socket.off('directory.user', onDirectoryUser);
    };
  }, [socket, currentUserId, notify]);
}

/** What the sign-in page says after the server ended the session. */
const REVOKED_NOTICES: Record<SessionRevoked['reason'], string> = {
  account_deleted: 'Your account was removed by an administrator.',
  account_disabled: 'Your account has been disabled. Contact your administrator.',
  session_revoked: 'You were signed out. Please sign in again.',
  session_expired: 'Your session has ended. Please sign in again.',
};
